<template>
  <component
    :is="tag"
    :type="props.to === undefined ? props.nativeType : undefined"
    :disabled="props.to === undefined ? props.disabled : undefined"
    :to="props.to !== undefined ? props.to : undefined"
    class="prepeers-button"
    :class="buttonClasses">
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ComputedRef, PropType } from 'vue'

import { NuxtLink } from '#components'
import type { RouteLocationRaw } from 'vue-router'
import type {
  PrePeersButtonProps,
  PrePeersButtonSize,
  PrePeersButtonVariant,
} from '~/types/PrePeersButton'

/**
 * Définit les props du composant PrePeersButton.
 */
const props: PrePeersButtonProps = defineProps({
  variant: {
    type: String as PropType<PrePeersButtonVariant>,
    default: 'primary',
  },
  size: {
    type: String as PropType<PrePeersButtonSize>,
    default: 'md',
  },
  nativeType: {
    type: String as PropType<'button' | 'submit' | 'reset'>,
    default: 'button',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  to: {
    type: [String, Object] as PropType<RouteLocationRaw | undefined>,
    default: undefined,
  },
})

const tag: ComputedRef<'button' | typeof NuxtLink> = computed((): 'button' | typeof NuxtLink => {
  return props.to !== undefined ? NuxtLink : 'button'
})

const buttonClasses: ComputedRef<string> = computed((): string => {
  const variant: PrePeersButtonVariant = props.variant ?? 'primary'
  const size: PrePeersButtonSize = props.size ?? 'md'

  let variantClass: string = 'btn btn-primary'
  if (variant === 'outline') {
    variantClass = 'btn btn-outline'
  } else if (variant === 'ghost') {
    variantClass = 'btn btn-ghost'
  } else if (variant === 'orange') {
    variantClass = 'btn btn-orange'
  } else if (variant === 'danger') {
    variantClass = 'btn btn-danger'
  }

  let sizeClass: string = ''
  if (size === 'sm') {
    sizeClass = 'btn-sm'
  } else if (size === 'lg') {
    sizeClass = 'btn-lg'
  } else if (size === 'xl') {
    sizeClass = 'btn-xl'
  }

  return [variantClass, sizeClass].filter((chunk: string) => chunk.length > 0).join(' ')
})
</script>

<style scoped>
.prepeers-button {
  text-decoration: none;
}
</style>
